import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Vapi from '@vapi-ai/web';
import { config as envConfig } from '@/env';
import { checkMicrophonePermission } from '@/helper/checkMicrophonePermission';
import { captureEvent } from '@/helper/postHog';
import { convertToSeconds } from '@/helper/formatTime';
import { SimulationConfig } from '@/pages/public/demo/types';
import { generateScenarioOptions } from '@/helper/generateAssistantOptions';
import { ChatMessage } from '@/types/socket';

const simulationStatus = {
  idle: 'idle',
  starting: 'starting',
  playing: 'playing',
  paused: 'paused',
  resuming: 'resuming',
  ended: 'ended',
  balanceLimitReached: 'balanceLimitReached'
} as const;

type SimulationStatus = (typeof simulationStatus)[keyof typeof simulationStatus];

export interface SimulationState {
  isPlaying: boolean;
  isPaused: boolean;
  isEnded: boolean;
  assistantIsSpeaking: boolean;
  isLoading: boolean;
  playButtonLabel: string;
  formattedDuration: string;
  error: string | null;
  status: SimulationStatus;
}

export interface SimulationCallbacks {
  onStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (message: ChatMessage) => void;
  onDurationUpdate?: (duration: string) => void;
  onBalanceDecrease?: (seconds: number) => void;
}

export interface UseSimulationReturn extends SimulationState {
  startSimulation: () => Promise<void>;
  pauseSimulation: () => void;
  resumeSimulation: () => Promise<void>;
  endSimulation: () => void;
  resetSimulation: () => void;
  callId: string | null;
  chatHistory: ChatMessage[];
}

export const useSimulation = (
  simulationConfig: SimulationConfig,
  callbacks: SimulationCallbacks = {}
): UseSimulationReturn => {
  const vapi = useRef<Vapi | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const lastDurationRef = useRef(0);
  const hasEndedRef = useRef(false);

  // State management
  const [status, setStatus] = useState<SimulationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [formattedDuration, setFormattedDuration] = useState('00:00:00');
  const [callId, setCallId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);

  const callLimit = simulationConfig.balance;

  // Timer management
  const stopTimer = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    hasEndedRef.current = false;
    intervalIdRef.current = setInterval(() => {
      setFormattedDuration(prevDuration => {
        const currentSeconds = convertToSeconds(prevDuration) + 1;
        const newDuration = new Date(currentSeconds * 1000).toISOString().slice(11, 19);
        lastDurationRef.current = currentSeconds;
        callbacks.onDurationUpdate?.(newDuration);

        // Check balance limit inside the state setter to ensure we have the correct currentSeconds
        const balanceInSeconds = convertToSeconds(callLimit);
        if (currentSeconds >= balanceInSeconds && !hasEndedRef.current) {
          hasEndedRef.current = true;

          // Use setTimeout to avoid setState during render
          setTimeout(() => {
            if (vapi.current) {
              vapi.current.stop();
            }
            stopTimer();
            setStatus(simulationStatus.balanceLimitReached);

            if (lastDurationRef.current > 0) {
              callbacks.onBalanceDecrease?.(lastDurationRef.current);
            }
            callbacks.onEnd?.();

            toast.info('Session ended - balance limit reached', {
              toastId: 'balance-limit-reached'
            });
          }, 0);
        }

        return newDuration;
      });
    }, 1000);
  }, [callbacks, stopTimer, callLimit]);

  // Error handling
  const handleError = useCallback(
    (err: any) => {
      stopTimer();
      setStatus(simulationStatus.ended);
      const errorMessage = err?.message || 'An error occurred during the simulation';
      setError(errorMessage);

      if (err?.action === 'error') {
        toast.error('The call was put on hold due to inactivity. Please resume when ready.', {
          toastId: 'inactivity-error'
        });
      } else if (err instanceof Error) {
        toast.error(err.message, { toastId: 'simulation-error' });
      } else if (err?.error?.message) {
        toast.error(err.error.message, { toastId: 'vapi-error' });
      }

      callbacks.onError?.(err);
    },
    [stopTimer, callbacks]
  );

  // Vapi event handlers
  const onStartCall = useCallback(() => {
    startTimer();
    setStatus(simulationStatus.playing);
    setError(null);
    callbacks.onStart?.();
  }, [startTimer, callbacks]);

  const onEndCall = useCallback(() => {
    stopTimer();
    if (lastDurationRef.current > 0) {
      callbacks.onBalanceDecrease?.(lastDurationRef.current);
    }
    callbacks.onEnd?.();
  }, [stopTimer, callbacks]);

  const onHandleMessage = useCallback(
    (message: any) => {
      if (message?.type === 'conversation-update' && message.conversation) {
        setChatHistory(prev => [...prev, message.conversation]);
        callbacks.onMessage?.(message);
      }
    },
    [callbacks]
  );

  // Vapi initialization
  const initializeVapi = useCallback(() => {
    if (vapi.current) {
      vapi.current.stop();
    }
    const vapiInstance = new Vapi(envConfig.VITE_VAPI_KEY);
    vapiInstance.on('call-start', onStartCall);
    vapiInstance.on('call-end', onEndCall);
    vapiInstance.on('speech-start', () => setAssistantIsSpeaking(true));
    vapiInstance.on('speech-end', () => setAssistantIsSpeaking(false));
    vapiInstance.on('error', handleError);
    vapiInstance.on('message', onHandleMessage);
    vapi.current = vapiInstance;
  }, [onStartCall, onEndCall, handleError, onHandleMessage]);

  const createAssistantAndStart = useCallback(async () => {
    try {
      const promptConfig = generateScenarioOptions(simulationConfig.scenario);

      const options = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${envConfig.VITE_VAPI_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(promptConfig)
      };

      const assistantResponse = await fetch(envConfig.VITE_VAPI_PLAY, options);
      const assistantData = await assistantResponse.json();

      if (!assistantResponse.ok || assistantData?.statusCode) {
        throw new Error(assistantData?.message || 'Failed to create assistant');
      }

      setCallId(assistantData.id);
      vapi.current?.start(assistantData.id);
    } catch (error) {
      handleError(error);
    }
  }, [simulationConfig.scenario, handleError]);

  // Simulation control methods
  const startSimulation = useCallback(async () => {
    try {
      if (convertToSeconds(callLimit) < 60) {
        toast.error('Insufficient balance to start the simulation', {
          toastId: 'insufficient-balance'
        });
        return;
      }

      const micPermission = await checkMicrophonePermission();
      if (!micPermission) {
        const errorMsg = 'Please enable microphone access in your browser and device settings';
        captureEvent('Simulation Error', {
          email: simulationConfig.user.email,
          scenario: simulationConfig.scenario,
          message: errorMsg
        });
        toast.error(errorMsg, { toastId: 'mic-error' });
        return;
      }

      setStatus(simulationStatus.starting);
      setError(null);

      initializeVapi();
      await createAssistantAndStart();

      captureEvent('Simulation start', {
        email: simulationConfig.user.email,
        scenario: simulationConfig.scenario
      });
    } catch (err) {
      handleError(err);
    }
  }, [simulationConfig, callLimit, initializeVapi, createAssistantAndStart, handleError]);

  const pauseSimulation = useCallback(() => {
    if (vapi.current && status === simulationStatus.playing) {
      vapi.current.stop();
      stopTimer();
      setStatus(simulationStatus.paused);
      callbacks.onPause?.();
    }
  }, [status, callbacks, stopTimer]);

  const resumeSimulation = useCallback(async () => {
    if (!vapi.current || status !== simulationStatus.paused) return;

    try {
      setStatus(simulationStatus.resuming);
      setError(null);

      if (chatHistory.length > 0) {
        // Resuming with context is complex. The Vapi SDK's start method
        // expects an assistant ID or a creation object. Passing a message
        // might be a specific feature. Preserving this logic.
        const lastMessage = chatHistory.at(-1);
        vapi.current.start(lastMessage?.content);
      } else {
        // If no history, we may need to start fresh.
        // Re-using the same callId to attempt a resume.
        if (callId) {
          vapi.current.start(callId);
        } else {
          await startSimulation();
        }
      }
      callbacks.onResume?.();
    } catch (err) {
      handleError(err);
    }
  }, [status, chatHistory, callId, startSimulation, handleError, callbacks]);

  const endSimulation = useCallback(() => {
    if (vapi.current) {
      vapi.current.stop();
    }
    stopTimer();
    setStatus(simulationStatus.ended);

    if (lastDurationRef.current > 0) {
      callbacks.onBalanceDecrease?.(lastDurationRef.current);
    }
    callbacks.onEnd?.();
  }, [stopTimer, callbacks]);

  const resetSimulation = useCallback(() => {
    if (vapi.current) {
      vapi.current.stop();
      vapi.current = null;
    }
    stopTimer();
    setStatus(simulationStatus.idle);
    setError(null);
    setAssistantIsSpeaking(false);
    setFormattedDuration('00:00:00');
    setCallId(null);
    setChatHistory([]);
    lastDurationRef.current = 0;
    hasEndedRef.current = false;
  }, [stopTimer]);

  useEffect(() => {
    return () => {
      vapi.current?.stop();
      stopTimer();
    };
  }, [stopTimer]);

  const derivedState = useMemo(
    () => ({
      isPlaying: status === simulationStatus.playing,
      isPaused: status === simulationStatus.paused,
      isEnded: status === simulationStatus.ended,
      isLoading: status === simulationStatus.starting || status === simulationStatus.resuming,
      playButtonLabel:
        status === simulationStatus.playing
          ? 'Pause'
          : status === simulationStatus.paused
            ? 'Resume'
            : status === simulationStatus.starting
              ? 'Starting...'
              : status === simulationStatus.resuming
                ? 'Resuming...'
                : 'Start'
    }),
    [status]
  );

  return {
    ...derivedState,
    status,
    assistantIsSpeaking,
    formattedDuration,
    error,
    startSimulation,
    pauseSimulation,
    resumeSimulation,
    endSimulation,
    resetSimulation,
    callId,
    chatHistory
  };
};
